import { useMemo, useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import HistoryIcon from '@mui/icons-material/History'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material'
import { GridEventListener, GridRenderCellParams } from '@mui/x-data-grid'
import { format, parseJSON } from 'date-fns'
import { toast } from 'react-toastify'

import { FplDataGrid, IGridColumn } from 'components/common'
import { DATE_FORMAT } from 'constants/index'
import { GET_LEGAL_DOCUMENTS, GET_LEGAL_DOCUMENTS_ALL } from 'graphql/queries'
import { SaveFile } from 'helpers'
import { DownloadLegalDocumentFile } from 'services/RestClient'

import { DOCUMENT_TYPE_FILTER_OPTIONS, DOCUMENT_TYPE_LABELS } from './constants'
import { LegalDocumentDialog } from './LegalDocumentDialog'
import { LegalDocumentPreview } from './LegalDocumentPreview'

const DOCUMENT_TYPES = DOCUMENT_TYPE_FILTER_OPTIONS

export const LegalDocumentsTab = () => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [filterType, setFilterType] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  const [historyFilterType, setHistoryFilterType] = useState('')
  const [previewContent, setPreviewContent] = useState<string | null>(null)
  const [previewTitle, setPreviewTitle] = useState('')

  const handleCreateDocument = () => {
    setSelectedDocumentId(null)
    setOpenDialog(true)
  }

  const handleDownloadFile = (uri: string, fileName: string) => {
    DownloadLegalDocumentFile(uri, fileName)
      .then((response) => SaveFile(response.data, fileName))
      .catch(() => toast.error('Failed to download file'))
  }

  const handleRowClick: GridEventListener<'rowClick'> = ({ id }) => {
    setSelectedDocumentId(Number(id))
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setSelectedDocumentId(null)
  }

  const activeFilter = useMemo(() => {
    const filter: Record<string, unknown> = {
      isActive: { eq: true },
    }
    if (filterType) {
      filter.documentType = { eq: filterType }
    }
    return filter
  }, [filterType])

  const historyFilter = useMemo(() => {
    const filter: Record<string, unknown> = {}
    if (historyFilterType) {
      filter.documentType = { eq: historyFilterType }
    }
    return Object.keys(filter).length > 0 ? filter : undefined
  }, [historyFilterType])

  const activeColumns: IGridColumn[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 200,
        flex: 1,
      },
      {
        field: 'documentType',
        headerName: 'Type',
        minWidth: 180,
        flex: 0.5,
        renderCell: ({ value }: GridRenderCellParams) => (
          <Chip
            label={DOCUMENT_TYPE_LABELS[value as string] || value}
            size='small'
            color='primary'
            variant='outlined'
          />
        ),
      },
      {
        field: 'version',
        headerName: 'Version',
        minWidth: 120,
        flex: 0.3,
        valueGetter: ({ row }: GridRenderCellParams) =>
          `${row.versionMajor ?? 0}.${row.versionMinor ?? 0}.${row.versionPatch ?? 0}`,
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        minWidth: 180,
        flex: 0.5,
        valueFormatter: ({ value }) =>
          value && format(parseJSON(value as string), DATE_FORMAT),
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        minWidth: 150,
        flex: 0.5,
      },
    ],
    [],
  )

  const historyColumns: IGridColumn[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 180,
        flex: 1,
      },
      {
        field: 'documentType',
        headerName: 'Type',
        minWidth: 160,
        flex: 0.5,
        renderCell: ({ value }: GridRenderCellParams) => (
          <Chip
            label={DOCUMENT_TYPE_LABELS[value as string] || value}
            size='small'
            color='primary'
            variant='outlined'
          />
        ),
      },
      {
        field: 'version',
        headerName: 'Version',
        minWidth: 100,
        flex: 0.3,
        valueGetter: ({ row }: GridRenderCellParams) =>
          `${row.versionMajor ?? 0}.${row.versionMinor ?? 0}.${row.versionPatch ?? 0}`,
      },
      {
        field: 'isActive',
        headerName: 'Status',
        minWidth: 100,
        flex: 0.3,
        renderCell: ({ value }: GridRenderCellParams) => (
          <Chip
            label={value ? 'Active' : 'Inactive'}
            size='small'
            color={value ? 'success' : 'default'}
          />
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        minWidth: 160,
        flex: 0.5,
        valueFormatter: ({ value }) =>
          value && format(parseJSON(value as string), DATE_FORMAT),
      },
      {
        field: 'deactivatedAt',
        headerName: 'Deactivated',
        minWidth: 160,
        flex: 0.5,
        valueFormatter: ({ value }) =>
          value ? format(parseJSON(value as string), DATE_FORMAT) : '-',
      },
      {
        field: 'content',
        headerName: 'Preview',
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: GridRenderCellParams) => (
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation()
              setPreviewContent(row.content)
              setPreviewTitle(`${row.name} v${row.versionMajor ?? 0}.${row.versionMinor ?? 0}.${row.versionPatch ?? 0}`)
            }}>
            <VisibilityIcon fontSize='small' />
          </IconButton>
        ),
      },
      {
        field: 'originalFileUri',
        headerName: 'File',
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: GridRenderCellParams) =>
          row.originalFileUri ? (
            <IconButton
              size='small'
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                handleDownloadFile(row.originalFileUri, row.originalFileName || 'document.docx')
              }}>
              <DownloadIcon fontSize='small' />
            </IconButton>
          ) : null,
      },
    ],
    [],
  )

  return (
    <Box sx={{ px: 2 }}>
      <Box sx={{ mb: 3 }}>
        <FplDataGrid
          query={GET_LEGAL_DOCUMENTS}
          entityName='legalDocuments'
          columns={activeColumns}
          onRowClick={handleRowClick}
          defaultOrder={{ field: 'createdAt', sort: 'desc' }}
          filter={activeFilter}
          toolbar={{
            caption: 'Active Legal Documents',
            rightSide: (
              <>
                <Grid item>
                  <TextField
                    select
                    size='small'
                    label='Filter by Type'
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    sx={{ minWidth: 180 }}>
                    {DOCUMENT_TYPES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item>
                  <Button
                    variant='contained'
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={handleCreateDocument}>
                    New Version
                  </Button>
                </Grid>
              </>
            ),
          }}
        />
      </Box>

      {!showHistory ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 1 }}>
          <Button
            variant='outlined'
            startIcon={<HistoryIcon />}
            onClick={() => setShowHistory(true)}>
            View History
          </Button>
        </Box>
      ) : (
        <FplDataGrid
          query={GET_LEGAL_DOCUMENTS_ALL}
          entityName='legalDocuments'
          columns={historyColumns}
          defaultOrder={{ field: 'createdAt', sort: 'desc' }}
          filter={historyFilter}
          toolbar={{
            caption: 'Version History',
            rightSide: (
              <>
                <Grid item>
                  <TextField
                    select
                    size='small'
                    label='Filter by Type'
                    value={historyFilterType}
                    onChange={(e) => setHistoryFilterType(e.target.value)}
                    sx={{ minWidth: 180 }}>
                    {DOCUMENT_TYPES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={() => setShowHistory(false)}>
                    Hide History
                  </Button>
                </Grid>
              </>
            ),
          }}
        />
      )}

      {previewContent !== null && (
        <Dialog
          open
          onClose={() => setPreviewContent(null)}
          maxWidth='md'
          fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {previewTitle}
            <IconButton onClick={() => setPreviewContent(null)} size='small'>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ lineHeight: 1.8, fontSize: '15px' }}>
              <LegalDocumentPreview content={previewContent} />
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {openDialog && (
        <LegalDocumentDialog
          id={selectedDocumentId}
          openDialog={openDialog}
          onCloseDialog={handleCloseDialog}
        />
      )}
    </Box>
  )
}
